import type { Query } from '@tanstack/react-query';

const SEASON_PATTERN = /^\d{4}\/\d{4}$/;

export const matchKeys = {
  all: ['matches'] as const,
  /** A season's match list; shares the root with the details, told apart by the season format. */
  list: (season: string) => ['matches', season] as const,
  detail: (id: string) => ['matches', id] as const,
  allStats: ['match-stats'] as const,
  stats: (
    signature: string,
    teamId: string | undefined,
    opponentTeamId: string | undefined,
    kind: string | undefined,
    season: string | undefined,
    championship?: string,
  ) =>
    [
      'match-stats',
      signature,
      teamId ?? 'all',
      opponentTeamId ?? 'all',
      kind ?? 'any',
      season ?? 'all',
      championship ?? 'all',
    ] as const,
};

/**
 * Filter of the season match lists only: invalidating `matchKeys.all` would also
 * refetch every match opened, its videos and favorites.
 */
export const matchListsFilter = {
  queryKey: matchKeys.all,
  predicate: (query: Pick<Query, 'queryKey'>) =>
    query.queryKey.length === 2 && typeof query.queryKey[1] === 'string' && SEASON_PATTERN.test(query.queryKey[1]),
};

export const featureFlagKeys = {
  user: (userId: string) => ['feature-flags', userId] as const,
};

export const contentViewKeys = {
  match: (matchId: string) => ['matches', matchId, 'views'] as const,
};

export const videoExportKeys = {
  list: (matchId: string) => ['matches', matchId, 'video-exports'] as const,
  logos: (matchId: string) => ['matches', matchId, 'video-export-logos'] as const,
};

export const notificationKeys = {
  all: ['notifications'] as const,
  list: () => ['notifications', 'list'] as const,
  unread: () => ['notifications', 'unread'] as const,
  preferences: () => ['notifications', 'preferences'] as const,
};

export const actionFavoriteKeys = {
  list: (matchId: string) => ['matches', matchId, 'action-favorites'] as const,
};

export const matchImportKeys = {
  draft: (draftId: string) => ['matches', 'import', draftId] as const,
};

export const opponentTeamKeys = {
  all: ['opponent-teams'] as const,
  detail: (id: string) => ['opponent-teams', id] as const,
};

export const teamKeys = {
  all: ['teams'] as const,
  detail: (teamId: string) => ['team', teamId] as const,
  players: (teamId: string) => ['team', teamId, 'players'] as const,
};

export const scoutingKeys = {
  /** Préfixe commun : sert aux invalidations en bloc (suppression d'un match…). */
  all: ['scouting'] as const,
  scoutedOpponents: (teamId: string, nameSearch: string) =>
    ['scouting', 'scouted-opponents', teamId, nameSearch] as const,
  opponentMatches: (teamId: string, opponentTeamId: string, isFinished?: boolean) =>
    ['scouting', 'opponent-matches', teamId, opponentTeamId, isFinished ?? 'all'] as const,
  opponentAggregate: (teamId: string, opponentTeamId: string) =>
    ['scouting', 'opponent-aggregate', teamId, opponentTeamId] as const,
  opponentTopPlayers: (teamId: string, opponentTeamId: string) =>
    ['scouting', 'opponent-top-players', teamId, opponentTeamId] as const,
  opponentVolleyStats: (teamId: string, opponentTeamId: string) =>
    ['scouting', 'opponent-volley-stats', teamId, opponentTeamId] as const,
  gamePlan: (teamId: string, opponentTeamId: string) =>
    ['scouting', 'game-plan', teamId, opponentTeamId] as const,
  gamePlans: (teamId: string) => ['scouting', 'game-plans', teamId] as const,
  opponentRoster: (
    teamId: string,
    opponentTeamId: string,
    filters: { since?: string; until?: string; limit: number; offset: number },
  ) => ['scouting', 'opponent-roster', teamId, opponentTeamId, filters] as const,
};

export const membershipKeys = {
  all: ['membership'] as const,
  invitations: (userId: string | undefined) =>
    ['membership', 'invitations', userId] as const,
  sentInvitations: (userId: string | undefined) =>
    ['membership', 'sent', userId] as const,
  pendingRequests: (userId: string | undefined) =>
    ['membership', 'pending', userId] as const,
  history: (userId: string | undefined) =>
    ['membership', 'history', userId] as const,
  managedMembers: (userId: string | undefined) =>
    ['membership', 'managed-members', userId] as const,
  clubs: ['membership', 'clubs'] as const,
  teams: ['membership', 'teams'] as const,
};
